/**
 * Property Search block — editor component.
 *
 * Search-first variant of the property archive. Shares the same server render
 * pipeline; the search bar is always shown on the frontend.
 *
 * @package
 * @since   3.5.0
 */

import { __ } from '@wordpress/i18n';
import { useBlockProps, InspectorControls } from '@wordpress/block-editor';
import {
	PanelBody,
	SelectControl,
	RangeControl,
	ToggleControl,
	TextControl,
	FormTokenField,
	Notice,
} from '@wordpress/components';
import BlockPreview from '../shared/BlockPreview';

const VIEW_OPTIONS = [
	{ label: __( 'Grid', 'havenlytics' ), value: 'grid' },
	{ label: __( 'List', 'havenlytics' ), value: 'list' },
	{ label: __( 'Map', 'havenlytics' ), value: 'map' },
];

const ORDERBY_OPTIONS = [
	{ label: __( 'Newest first', 'havenlytics' ), value: 'date' },
	{ label: __( 'Title (A–Z)', 'havenlytics' ), value: 'title' },
	{ label: __( 'Price: low to high', 'havenlytics' ), value: 'price_low' },
	{ label: __( 'Price: high to low', 'havenlytics' ), value: 'price_high' },
	{ label: __( 'Random', 'havenlytics' ), value: 'rand' },
];

const SIDEBAR_POSITION_OPTIONS = [
	{ label: __( 'Left', 'havenlytics' ), value: 'left' },
	{ label: __( 'Right', 'havenlytics' ), value: 'right' },
];

function cleanTokens( tokens ) {
	if ( ! Array.isArray( tokens ) ) {
		return [];
	}

	return tokens
		.map( ( token ) => String( token ).trim() )
		.filter( ( token ) => token.length > 0 );
}

export default function Edit( { attributes, setAttributes } ) {
	const {
		defaultView,
		columns,
		postsPerPage,
		orderby,
		featuredOnly,
		showFilterSidebar,
		sidebarPosition,
		department,
		propertyType,
		status,
		location,
	} = attributes;

	const blockProps = useBlockProps();

	return (
		<>
			<InspectorControls>
				<PanelBody
					title={ __( 'Layout', 'havenlytics' ) }
					initialOpen={ true }
				>
					<p className="components-base-control__help">
						{ __(
							'Search-first archive. Same filters and cards as Property Archive.',
							'havenlytics'
						) }
					</p>
					<SelectControl
						label={ __( 'Default view', 'havenlytics' ) }
						value={ defaultView }
						options={ VIEW_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { defaultView: value } )
						}
						__nextHasNoMarginBottom
					/>
					<RangeControl
						label={ __( 'Columns', 'havenlytics' ) }
						value={ columns }
						min={ 1 }
						max={ 4 }
						onChange={ ( value ) =>
							setAttributes( { columns: value } )
						}
						help={ __( 'Applies to grid view.', 'havenlytics' ) }
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __( 'Show filter sidebar', 'havenlytics' ) }
						checked={ !! showFilterSidebar }
						onChange={ ( value ) =>
							setAttributes( { showFilterSidebar: value } )
						}
						__nextHasNoMarginBottom
					/>
					{ showFilterSidebar && (
						<SelectControl
							label={ __( 'Sidebar position', 'havenlytics' ) }
							value={ sidebarPosition }
							options={ SIDEBAR_POSITION_OPTIONS }
							onChange={ ( value ) =>
								setAttributes( { sidebarPosition: value } )
							}
							__nextHasNoMarginBottom
						/>
					) }
				</PanelBody>

				<PanelBody
					title={ __( 'Query', 'havenlytics' ) }
					initialOpen={ false }
				>
					<RangeControl
						label={ __( 'Properties per page', 'havenlytics' ) }
						value={ postsPerPage }
						min={ 1 }
						max={ 48 }
						onChange={ ( value ) =>
							setAttributes( { postsPerPage: value } )
						}
						__nextHasNoMarginBottom
					/>
					<SelectControl
						label={ __( 'Order by', 'havenlytics' ) }
						value={ orderby }
						options={ ORDERBY_OPTIONS }
						onChange={ ( value ) =>
							setAttributes( { orderby: value } )
						}
						__nextHasNoMarginBottom
					/>
					<ToggleControl
						label={ __(
							'Featured properties only',
							'havenlytics'
						) }
						checked={ !! featuredOnly }
						onChange={ ( value ) =>
							setAttributes( { featuredOnly: value } )
						}
						__nextHasNoMarginBottom
					/>
				</PanelBody>

				<PanelBody
					title={ __( 'Taxonomy filters', 'havenlytics' ) }
					initialOpen={ false }
				>
					<Notice status="info" isDismissible={ false }>
						{ __(
							'Enter term slugs to pre-filter results. Visitors can still refine with the search filters.',
							'havenlytics'
						) }
					</Notice>
					<TextControl
						label={ __( 'Department (slug)', 'havenlytics' ) }
						value={ department }
						onChange={ ( value ) =>
							setAttributes( { department: value } )
						}
						__nextHasNoMarginBottom
					/>
					<FormTokenField
						label={ __( 'Property types (slugs)', 'havenlytics' ) }
						value={ propertyType }
						onChange={ ( tokens ) =>
							setAttributes( {
								propertyType: cleanTokens( tokens ),
							} )
						}
					/>
					<FormTokenField
						label={ __( 'Status (slugs)', 'havenlytics' ) }
						value={ status }
						onChange={ ( tokens ) =>
							setAttributes( { status: cleanTokens( tokens ) } )
						}
					/>
					<FormTokenField
						label={ __( 'Locations (slugs)', 'havenlytics' ) }
						value={ location }
						onChange={ ( tokens ) =>
							setAttributes( { location: cleanTokens( tokens ) } )
						}
					/>
				</PanelBody>
			</InspectorControls>

			<div { ...blockProps }>
				<BlockPreview
					block="havenlytics/property-search"
					attributes={ attributes }
				/>
			</div>
		</>
	);
}
